import { expectAtIndex } from "@aprao/common/src/array/expectAtIndex.js";
import { assertUnreachable } from "@aprao/common/src/assertUnreachable.js";
import type { BigFraction } from "@aprao/common/src/math/BigFraction.js";
import { bigFractionFromFraction } from "@aprao/common/src/math/BigFraction.js";
import { divide } from "@aprao/common/src/math/divide.js";
import { FRACTION_ZERO } from "@aprao/common/src/math/fraction.js";
import {
  abbreviateNumber,
  formatDecimalNumber,
  formatPercentage,
} from "@aprao/common/src/numberFormats.js";
import type { PeriodGranularity } from "@aprao/common/src/shapes/common.js";
import { times } from "@aprao/common/src/util/times.js";
import { css } from "@emotion/react";
import { useTheme } from "@mui/material/styles";
import chroma from "chroma-js";
import { mapForNullable } from "option-t/nullable";
import { mapOrForUndefinable } from "option-t/undefinable";
import type { ComponentPropsWithoutRef, ReactNode } from "react";
import { UnreachableCaseError } from "ts-essentials";

import { useI18n } from "../ApraoI18n.js";
import { useNumberUnits } from "../useNumberUnits.js";
import type {
  AnalysisOutputKind,
  ProducerOutputTypeMap,
} from "./outputProducersRegistry.js";
import type {
  CloneableSensitivityCategory,
  SensitivityCategories,
  SensitivityWorkerResult,
} from "./sensitivityWorker.js";

const red = "hsl(0,73%,77%)";
const green = "hsl(123,38%,57%)";

const criticalReturn = 0.1;
const goodReturn = 0.25;
const scaleRoc = chroma
  .scale([red, green])
  .mode("lab")
  .domain([criticalReturn, goodReturn]);

const colorProviders = {
  roc: (value: BigFraction) => scaleRoc(value.valueOf()).css(),
  profit: (value: BigFraction) => (value.isNeg() ? red : green),
  residual: () => "initial" as const,
};

const sizeSingleCell = 32;
const stylesRow = css`
  height: ${sizeSingleCell}px;
`;

const stylesHeader = css`
  font-weight: normal;
`;

const stylesBordered = css`
  border: var(--border);
`;

const stylesDataCell = css`
  font-weight: bold;
  text-align: center;
  color: hsla(0, 0%, 0%, 0.87);
`;

type CellData = {
  [kind in AnalysisOutputKind]: {
    kind: kind;
    value: ProducerOutputTypeMap[kind];
  };
}[AnalysisOutputKind];

const ResidualDualDisplay = (props: {
  startSlot: ReactNode;
  endSlot: ReactNode;
}) => {
  const { palette } = useTheme();

  return (
    <>
      <div>{props.startSlot}</div>
      <div
        css={css`
          line-height: 1.5cap;

          font-weight: normal;
          color: ${palette.text.secondary};
        `}
      >
        {props.endSlot}
      </div>
    </>
  );
};

interface CellProps extends ComponentPropsWithoutRef<"td"> {
  cellData: CellData;
  dense?: boolean;
}

const Cell = (props: CellProps) => {
  const { cellData, dense, ...others } = props;

  const { effectiveLocale } = useI18n();

  let bgColor = "";
  let content;
  if (cellData.kind === "roc" || cellData.kind === "profit") {
    const profit = bigFractionFromFraction(cellData.value.profit);
    const roc =
      divide(profit, bigFractionFromFraction(cellData.value.costs)) ??
      FRACTION_ZERO;

    bgColor = colorProviders.roc(roc);

    if (cellData.kind === "roc") {
      content = formatPercentage(roc, true);
    } else if (cellData.kind === "profit") {
      content = abbreviateNumber(effectiveLocale, profit);
    }
  } else if (cellData.kind === "residual") {
    content = (
      <ResidualDualDisplay
        startSlot={abbreviateNumber(
          effectiveLocale,
          bigFractionFromFraction(cellData.value.residual),
        )}
        endSlot={
          !dense &&
          abbreviateNumber(
            effectiveLocale,
            bigFractionFromFraction(cellData.value.profit),
          )
        }
      />
    );
  } else {
    throw new UnreachableCaseError(cellData);
  }

  return (
    <td
      css={css`
        background-color: ${bgColor};
      `}
      {...others}
    >
      {content}
    </td>
  );
};

const formatCategoryValue = (
  value: CloneableSensitivityCategory["displayValue"],
  isValuePercentage: boolean,
  effectiveLocale: Intl.Locale,
) =>
  mapForNullable(value, (_value) =>
    isValuePercentage ?
      formatPercentage(bigFractionFromFraction(_value))
    : abbreviateNumber(effectiveLocale, bigFractionFromFraction(_value)),
  ) ?? "N/A";

const formatCategoryIncrement = (
  increment: CloneableSensitivityCategory["increment"],
  kind: SensitivityCategories["incrementKind"],
) =>
  kind === "scale" ? formatPercentage(bigFractionFromFraction(increment), true)
  : kind === "offset" ? formatDecimalNumber(bigFractionFromFraction(increment))
  : assertUnreachable(kind);

const labelByPeriodGranularity = {
  annual: "year",
  monthly: "month",
  weekly: "week",
} as const satisfies Record<PeriodGranularity, string>;

const CategoryLabel = (props: {
  categories: SensitivityCategories;
  dense?: boolean;
}) => {
  const { categories, dense } = props;

  const { msg } = useI18n();
  const { lengthUnit } = useNumberUnits();

  const shouldShowUnit =
    !dense && (categories.kind === "buildCosts" || categories.kind === "rent");

  return (
    <>
      {msg(`sensitivity.${categories.kind}Label`)}
      {shouldShowUnit && (
        <>
          {" "}
          (
          {msg(`sensitivity.${categories.kind}Unit`, {
            unit: lengthUnit.symbol,
            periodLabel: mapOrForUndefinable(
              categories.labelParams?.periodGranularity,
              "",
              (granularity) => labelByPeriodGranularity[granularity],
            ),
          })}
          )
        </>
      )}
    </>
  );
};

const Row = (props: {
  sensitivity: SensitivityWorkerResult<AnalysisOutputKind>;
  rowIndex: number;
  dense?: boolean;
}) => {
  const { sensitivity, rowIndex, dense } = props;

  const stylesCell = [stylesBordered, stylesDataCell];

  const { effectiveLocale } = useI18n();

  const isFirstRow = rowIndex === 0;
  const yCategory = expectAtIndex(
    sensitivity.grid.yCategories.categories,
    rowIndex,
  );
  const row = expectAtIndex(sensitivity.grid.matrix, rowIndex);

  return (
    <tr css={stylesRow}>
      {isFirstRow && (
        <th
          css={[stylesHeader, stylesBordered]}
          rowSpan={sensitivity.grid.yCategories.categories.length}
        >
          <div
            css={css`
              width: 100%;
              display: flex;
              align-items: center;
              writing-mode: vertical-lr;
              transform: rotateZ(0.5turn);
              white-space: nowrap;
            `}
          >
            <CategoryLabel
              categories={sensitivity.grid.yCategories}
              dense={dense}
            />
          </div>
        </th>
      )}
      <th css={[stylesHeader, stylesBordered]}>
        {formatCategoryIncrement(
          yCategory.increment,
          sensitivity.grid.yCategories.incrementKind,
        )}
      </th>
      {!dense && (
        <th css={[stylesHeader, stylesBordered]}>
          {formatCategoryValue(
            yCategory.displayValue,
            sensitivity.grid.yCategories.isValuePercentage,
            effectiveLocale,
          )}
        </th>
      )}
      {row.map((cellValue, idx) => {
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        const cellData = {
          kind: sensitivity.analysis.output,
          value: cellValue,
        } as CellData;

        return (
          <Cell key={idx} css={stylesCell} cellData={cellData} dense={dense} />
        );
      })}
    </tr>
  );
};

export const SensitivityTable = (
  props: ComponentPropsWithoutRef<"table"> & {
    sensitivity: SensitivityWorkerResult<AnalysisOutputKind>;
    caption?: ReactNode;
    borderLess?: boolean;
    // hide the axis absolute values (only show the percentages)
    dense?: boolean;
  },
) => {
  const { sensitivity, caption, borderLess, dense, ...others } = props;

  const { effectiveLocale } = useI18n();

  return (
    <table
      css={[
        css`
          inline-size: 100%;
          table-layout: fixed;
          border-collapse: collapse;

          th,
          td {
            padding: 0;
          }

          @media print {
            break-inside: avoid;
          }
        `,
        !borderLess &&
          css`
            --border: 1px solid hsl(0, 0%, 83%);
          `,
      ]}
      {...others}
    >
      {caption}
      <colgroup
      //we need colgroup to set the columns width after the section with colspan
      >
        <col
          css={css`
            width: ${sizeSingleCell}px;
          `}
        />
        <col
          css={css`
            width: ${sizeSingleCell}px;
          `}
        />
        {!dense && <col />}
      </colgroup>
      <tbody css={stylesBordered}>
        <tr css={stylesRow}>
          <td
            css={css`
              text-align: center;
            `}
            colSpan={dense ? 2 : 3}
            rowSpan={dense ? 2 : 3}
          >
            {!dense && sensitivity.analysis.output === "residual" && (
              <ResidualDualDisplay
                startSlot="Residual value"
                endSlot="Profit"
              />
            )}
          </td>
          <th
            css={[stylesHeader, stylesBordered]}
            colSpan={sensitivity.grid.xCategories.categories.length}
          >
            <CategoryLabel
              categories={sensitivity.grid.xCategories}
              dense={dense}
            />
          </th>
        </tr>
        <tr css={stylesRow}>
          {sensitivity.grid.xCategories.categories.map((xCategory, idx) => (
            <th key={idx} css={[stylesHeader, stylesBordered]}>
              {formatCategoryIncrement(
                xCategory.increment,
                sensitivity.grid.xCategories.incrementKind,
              )}
            </th>
          ))}
        </tr>
        {!dense && (
          <tr css={stylesRow}>
            {sensitivity.grid.xCategories.categories.map((xCategory, idx) => (
              <th key={idx} css={[stylesHeader, stylesBordered]}>
                {formatCategoryValue(
                  xCategory.displayValue,
                  sensitivity.grid.xCategories.isValuePercentage,
                  effectiveLocale,
                )}
              </th>
            ))}
          </tr>
        )}
        {times(sensitivity.grid.matrix.length, (rowIndex) => (
          <Row
            key={rowIndex}
            rowIndex={rowIndex}
            sensitivity={sensitivity}
            dense={dense}
          />
        ))}
      </tbody>
    </table>
  );
};
